import { useQuery } from 'react-query';
import { authFetch } from '../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';

export enum IncomeType {
  Salary,
  Bonus,
  E401KContribution,
  Pension,
  Business,
  RealEstate,
  SocialSecurity,
  Other,
}

export enum IncomeFrequency {
  Yearly,
  Monthly,
}

export interface OwnerModelDTO {
  Id: string | null;
  Name: string;
  OwnerType: OwnerTypeDTO;
  Percentage: number;
}

export interface IncomeModel {
  Id: string;
  Name: string;
  IncomeType: IncomeType | null;
  Earner: string;
  Amount: number | null;
  IncomeFrequency: IncomeFrequency | null;
  IsSpouse: boolean;
  BonusCommissions: number | null;
  AssociatedAssetId: string;
  ContinuesPostRetirement: boolean | null;
  StartAge: number | null;
  EndAge: number | null;
  Inflation: number | null;
  IsTaxFree: boolean | null;
  IncomeGrowth: number | null;
  SelfEmployed: boolean | null;
  AccountEarmark: { Type: string; Tooltip: string }[];
  YearlyTaxableAmount: number | null;
  EndAtRetirement: boolean | null;
  EndAtEndOfPlan: boolean | null;
  CurrentlyReceivingBenefits: boolean;
  UseFra: boolean | null;
  OrionManagementStyleId: number | null;
  CustomBucketIndex: number | null;
  OwnerModel: OwnerModelDTO[];
}

export interface ManagementStyleDTO {
  id: number;
  name: string;
  defaultBucket: number | null;
}

interface AvailableAssetBucketsDTO {
  AssetTypes: number[];
  Description: string;
  ManagementStyles: number[];
  Title: string;
}

export enum OwnerTypeDTO {
  Principal = 0,
  Spouse = 1,
  Joint = 2,
  Trust = 3,
  Business = 4,
  Child = 5,
  Grandchild = 6,
  OtherDependent = 7,
  OtherBeneficiary = 8,
  OtherIndividual = 9,
}

export interface OwnerOptionDTO {
  Id: string | null;
  Name: string;
  OwnerType: OwnerTypeDTO;
}

export interface RealEstateAssetOptionDTO {
  label: string;
  value: string;
  ownerModel?: OwnerModelDTO[];
}

export interface OwnerOptionsDTO {
  People: OwnerOptionDTO[];
  Trusts: OwnerOptionDTO[] | null;
  Businesses: OwnerOptionDTO[] | null;
}

export interface IncomeOptionsDTO {
  BusinessAssetOption: { label: string; value: string }[];
  EarmarkOptions: { label: string; value: string }[];
  EndYearOptions: number[];
  IncomeFrequencyOptions: { label: string; value: IncomeFrequency }[];
  IncomeTypeOptions: { label: string; value: IncomeType }[];
  OtherIncomeEndYearOptions: number[];
  OwnerOptions: OwnerOptionsDTO;
  RealEstateAssetOptions: RealEstateAssetOptionDTO[];
  StartYearOptions: number[];
  PrincipalSocialSecurityAgeOptions: number[];
  SpouseSocialSecurityAgeOptions: number[];
  PrincipalFra: { Year: number; Month: number };
  SpouseFra: { Year: number; Month: number };
  AssetBucketingByManagementStyle: boolean;
  ManagementStyles: ManagementStyleDTO[];
  AllowAssetBucketingCustomization: boolean;
  AvailableAssetBuckets: AvailableAssetBucketsDTO[];
  DisplayAssetBucketing: boolean;
  PrincipalAgesOptions: { Age: number; Label: string; SubLabel: string }[];
  SpouseAgesOptions: { Age: number; Label: string; SubLabel: string }[];
}

export interface IncomeDTO {
  Incomes: IncomeModel[];
  Options: IncomeOptionsDTO;
  SocialSecurityUseSpousalBenefit: boolean;
}

interface FetchParams {
  clientId: string;
}

export const fetchIncome = async ({ clientId }: FetchParams): Promise<IncomeDTO> => {
  return authFetch(urlWithParams('/Consumer/IncomeStep', { clientId }));
};

interface QueryParams {
  clientId: string;
}

export const useIncomeQuery = ({ clientId }: QueryParams) => {
  return useQuery(
    queryKeys.income(clientId),

    () => fetchIncome({ clientId }),

    {
      cacheTime: 0,
    },
  );
};
